import React from "react";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { TrackedDownload } from "./DownloadsProvider";

interface Props {
    downloads: TrackedDownload[];
    /** Open the terms/download modal for a ready download. */
    onOpen: (roomId: string) => void;
    /** Remove a download from the tracker. */
    onDismiss: (roomId: string) => void;
}

/**
 * Slim, fixed bar at the top of the viewport shown while a long-running
 * zip is being prepared in the background. Non-blocking by design — it
 * replaces the full-screen overlay once a download is promoted.
 */
export function DownloadTopBar({ downloads, onOpen, onDismiss }: Props) {
    if (!downloads.length) return null;

    const ready = downloads.filter((d) => d.status === "ready");
    const inProgress = downloads.filter((d) => d.status === "in_progress");
    const stale = downloads.filter((d) => d.status === "stale");

    let content: React.ReactNode;
    if (ready.length) {
        content = (
            <>
                <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    Your download is ready
                    {ready.length > 1 ? ` (${ready.length})` : ""}
                    {inProgress.length ? ` · ${inProgress.length} more still preparing…` : ""}
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ ml: 1, py: 0.25, textTransform: "none", flexShrink: 0 }}
                    onClick={() => onOpen(ready[0].roomId)}
                >
                    Download
                </Button>
                <IconButton size="small" aria-label="Dismiss download" onClick={() => onDismiss(ready[0].roomId)}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </>
        );
    } else if (inProgress.length) {
        content = (
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {inProgress.length > 1
                    ? `Preparing ${inProgress.length} downloads… you can keep working`
                    : "Preparing your download… you can keep working"}
            </Typography>
        );
    } else {
        content = (
            <>
                <ErrorOutlineIcon color="warning" sx={{ fontSize: 20 }} />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {stale.length > 1 ? "Some downloads are" : "A download is"} taking longer than expected. It may
                    still complete in the background.
                </Typography>
                <Button
                    size="small"
                    sx={{ ml: 1, textTransform: "none", flexShrink: 0 }}
                    onClick={() => stale.forEach((d) => onDismiss(d.roomId))}
                >
                    Dismiss
                </Button>
            </>
        );
    }

    return (
        <Box
            role="status"
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1350,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                px: 2,
                minHeight: 44,
                bgcolor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "@keyframes downloadBarSlideDown": {
                    from: { transform: "translateY(-100%)" },
                    to: { transform: "translateY(0)" }
                },
                animation: "downloadBarSlideDown 200ms ease-out"
            }}
        >
            {content}
            {!ready.length && inProgress.length > 0 && (
                <LinearProgress
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 2
                    }}
                />
            )}
        </Box>
    );
}
